// @flow
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import AdapterJalali from '@date-io/date-fns-jalali';
import { LocalizationProvider, DatePicker as MuiDatePicker } from '@mui/x-date-pickers';
import type { datePickerType } from '@shared/model/components';
import { useState } from 'react';
import findNestedValue from '@shared/utils/findNestedValue';
import convertDate from '@shared/utils/convertDate';
import { format, newDate } from 'date-fns-jalali';
import { Clear, Event } from '@mui/icons-material';
import HelperText from '@shared/components/helperText';

function DatePicker({
	id,
	label,
	name,
	control,
	errors,
	schema,
	defaultValue = '',
	comment,
	mask = '____/__/__',
	gregorianValue = false,
	disableKeyBoardInput = false,
	...datePickerProps
}: datePickerType): React$Node {

	const [isRequired] = useState(
		schema?.describe()?.fields[name]?.tests?.some((test) => test.name === 'required') || false,
	);

	const { disabled, ...otherProps } = datePickerProps;

	return (
		<Controller
			name={name}
			defaultValue={defaultValue}
			control={control}
			render={({ field }) => {
				let newField = { ...field };

				if (!gregorianValue) {
					const updateValue = value => {
						let newValue = '';
						try {
							newValue = format(value,'yyyy/MM/d');
						} catch (error) {}

						field.onChange(newValue || value);
					};

					newField = { ...newField, onChange: updateValue };

					if (newField?.value && typeof newField?.value === 'string') {
						if (!disabled) {
							const { year, month, day, hours, minutes } = convertDate(newField?.value);
							const date = newDate(year, month - 1, day, hours, minutes);
							newField.value = date;
						} else {
							const notConvertedValue = newField?.value?.substring(0, 10);
							newField.value = notConvertedValue;
						}
					}
				}

				return (
					<Box>
						{
							disabled ?
								<TextField
									{...newField}
									label={label}
									variant={'filled'}
									fullWidth
									disabled
									InputProps={{
										startAdornment: <InputAdornment position={'start'}>
											<Event />
										</InputAdornment>,
									}}
								/> :
								<LocalizationProvider dateAdapter={AdapterJalali}>
									<MuiDatePicker
										mask={mask}
										{...newField}
										{...otherProps}
										onAccept={(date) => {
											const formatted = format(date,'yyyy/MM/dd');
											newField.onChange(formatted);
										}}
										OpenPickerButtonProps={{
											...(id && { id: `${id}-open-picker-button` }),
										}}
										inputFormat={'yyyy/MM/d'}
										renderInput={(params: any) => {
											const { error, ...textFieldParams } = params;
											const foundError = findNestedValue(errors, name);

											const formatOnBlur = ({ target: { value } }) => {
												let newValue = '';

												try {
													newValue = format(newField.value,'yyyy/MM/dd');
												} catch (error) {}

												field.onChange(newValue || value);
											};

											return (<TextField
												{...textFieldParams}
												label={label}
												{...(!gregorianValue && newField.value && { onBlur: formatOnBlur })}
												required={isRequired}
												error={Boolean(foundError)}
												helperText={<HelperText message={foundError?.message || comment}/>}
												InputLabelProps={{ shrink: true }}
												InputProps={{
													...textFieldParams.InputProps,
													...(newField.value && {
														startAdornment: (
															<InputAdornment position={'start'}>
																<IconButton
																	{...(id ? { id: `${id}-clear-date` } : {})}
																	onClick={() => {
																		newField.onChange(null);
																	}}
																>
																	<Clear fontSize={'small'} />
																</IconButton>
															</InputAdornment>
														),
													}),
													inputProps: {
														...textFieldParams.inputProps,
														placeholder: mask,
														id,
														...(disableKeyBoardInput && {
															readOnly: true,
															tabIndex: -1,
														}),
													},
												}}
												sx={{
													'.MuiInputBase-root': {
														flexDirection: 'row-reverse',
														alignItems: 'center',
														...(newField.value && {
															gap: '14px', p: 0,
														}),
													},
													'.MuiInputBase-input': {
														direction: 'rtl', textAlign: 'left',
													},
												}}
												fullWidth
											/>);
										}}
									/>
								</LocalizationProvider>
						}
					</Box>
				);
			}}
		/>
	);
}

export default DatePicker;
